// # Weak Spots Component for Step 1 Mentorship
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { 
  Paper, Typography, Box, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Checkbox, Dialog, DialogTitle, DialogContent,
  DialogActions, DialogContentText, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Snackbar, Alert, IconButton,
  TextareaAutosize, Divider
} from '@mui/material';
import { 
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  BarChart as BarChartIcon, Timeline as TimelineIcon,
  ArrowUpward as ArrowUpwardIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer
} from 'recharts';
// # Import library navigation icon
import { FaBookMedical } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './weakspots1.css';

// # Import Firebase
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import cacheManager from '../../../auth/cache_manager';

// # Generate a unique ID for weak spots
const generateRandomId = () => {
  // # Format: weak_ISO-timestamp_random6chars
  const timestamp = new Date().toISOString();
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let randomStr = '';
  for (let i = 0; i < 6; i++) {
    randomStr += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `weak_${timestamp}_${randomStr}`;
};

// # Chapter definitions with color coding for Step 1 only
const STEP1_CHAPTERS = [
  { name: 'Biochemistry', color: '#FFE0E0' },
  { name: 'Immunology', color: '#E0FFE0' },
  { name: 'Microbiology', color: '#E0E0FF' },
  { name: 'General Pathology', color: '#FFE0FF' },
  { name: 'Pharmacology', color: '#E0FFFF' },
  { name: 'Public Health Sciences', color: '#FFFDE0' },
  { name: 'Anatomy & histology', color: '#FFE0E8' },
  { name: 'Cardiovascular', color: '#E0FFE8' },
  { name: 'Endocrine', color: '#E0E8FF' },
  { name: 'Gastrointestinal', color: '#FFE8E0' },
  { name: 'Hematology and Oncology', color: '#E8FFE0' },
  { name: 'Musculoskeletal and Connective Tissue', color: '#E8E0FF' },
  { name: 'Dermatology', color: '#FFE8FF' },
  { name: 'Neurology', color: '#E8FFFF' },
  { name: 'Special Senses', color: '#FFFFE8' },
  { name: 'Psychiatry', color: '#F0E0E0' },
  { name: 'Renal', color: '#E0F0E0' },
  { name: 'Reproductive', color: '#E0E0F0' },
  { name: 'Respiratory', color: '#F0E0F0' },
  { name: 'Rapid Review', color: '#E0F0F0' },
  { name: 'Sketchy Micro', color: '#F0F0E0' }
];

function WeakSpots1({ isAdmin }) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [weakSpotsMap, setWeakSpotsMap] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [deletingSpot, setDeletingSpot] = useState(null);
  const [viewingSpot, setViewingSpot] = useState(null);
  const [formData, setFormData] = useState({
    topic: '',
    chapter: '',
    details: '',
    chapterColor: '#E0E0FF',
    step: 'Step 1',
    practiced: false,
    mastered: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [showStats, setShowStats] = useState(false);
  // # Add state for target user ID and mentor status
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Navigate to library function
  const navigateToLibrary = () => {
    navigate('/library/step1');
  };

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    setTargetUserId(menteeId || user?.uid);
    setIsMentor(!!menteeId);
  }, [user?.uid]);

  // # Load weak spots data from Firestore
  useEffect(() => {
    const loadWeakSpots = async () => {
      if (!targetUserId) return;

      setIsLoading(true);
      try {
        // # Get data from s1_mentee collection
        const docRef = doc(db, 's1_mentee', targetUserId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          // # Get weakSpots map or default to empty object
          const data = docSnap.data();
          setWeakSpotsMap(data?.weakSpots || {});
        } else {
          // # If document doesn't exist, initialize with empty object
          setWeakSpotsMap({});
        }
      } catch (error) {
        console.error('Error loading weak spots:', error);
        setSnackbar({
          open: true,
          message: 'Error loading weak spots. Please try again.',
          severity: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadWeakSpots();
  }, [targetUserId]);

  // # Save weak spots to Firestore
  const saveWeakSpots = async (updatedSpotsMap) => {
    if (!targetUserId) return;

    try {
      const docRef = doc(db, 's1_mentee', targetUserId);
      const docSnap = await getDoc(docRef);
      const currentData = docSnap.exists() ? docSnap.data() : {};

      // # Update the weakSpots field while preserving other data
      await setDoc(docRef, {
        ...currentData,
        weakSpots: updatedSpotsMap
      }, { merge: true });
      
      return true;
    } catch (error) {
      console.error('Error saving weak spots:', error);
      return false;
    }
  };

  // # Handle add button click
  const handleAddClick = () => {
    setEditingSpot(null);
    setFormData({ 
      chapter: '', 
      topic: '', 
      details: '',
      chapterColor: '#E0E0FF', 
      step: 'Step 1', 
      practiced: false, 
      mastered: false 
    });
    setOpenDialog(true);
  };

  // # Handle edit button click
  const handleEditClick = (spot) => {
    setEditingSpot(spot);
    setFormData({
      chapter: spot.chapter,
      topic: spot.topic,
      details: spot.details || '',
      chapterColor: spot.chapterColor,
      step: spot.step,
      practiced: spot.practiced,
      mastered: spot.mastered
    });
    setOpenDialog(true);
  };

  // # Handle details button click
  const handleDetailsClick = (spot) => {
    setViewingSpot(spot);
    setFormData({
      ...formData,
      details: spot.details || ''
    });
    setOpenDetailsDialog(true);
  };

  // # Handle delete button click
  const handleDeleteClick = (spot) => {
    setDeletingSpot(spot);
    setOpenDeleteDialog(true);
  };

  // # Handle form submission
  const handleFormSubmit = async () => {
    if (!formData.chapter || !formData.topic) return;
    
    setIsSaving(true);
    const timestamp = new Date().toISOString();
    
    try {
      let updatedSpotsMap;
      
      if (editingSpot) {
        // # Update existing spot
        updatedSpotsMap = {
          ...weakSpotsMap,
          [editingSpot.id]: {
            ...editingSpot,
            chapter: formData.chapter,
            topic: formData.topic,
            details: formData.details || '',
            chapterColor: formData.chapterColor || STEP1_CHAPTERS.find(ch => ch.name === formData.chapter)?.color || '#E0E0FF',
            step: formData.step,
            practiced: formData.practiced,
            mastered: formData.mastered,
            updatedAt: timestamp
          }
        };
      } else {
        // # Add new spot
        const newId = generateRandomId();
        const newSpot = {
          id: newId,
          chapter: formData.chapter,
          topic: formData.topic,
          details: formData.details || '',
          chapterColor: formData.chapterColor || STEP1_CHAPTERS.find(ch => ch.name === formData.chapter)?.color || '#E0E0FF',
          step: formData.step || 'Step 1',
          reviewCount: 0,
          practiced: formData.practiced,
          mastered: formData.mastered,
          createdAt: timestamp,
          updatedAt: timestamp
        };
        
        updatedSpotsMap = {
          ...weakSpotsMap,
          [newId]: newSpot
        };
      }
      
      // # Save to Firestore
      const success = await saveWeakSpots(updatedSpotsMap);
      
      if (success) {
        // # Update local state
        setWeakSpotsMap(updatedSpotsMap);
        
        // # Close dialog and show success message
        setOpenDialog(false);
        setFormData({ chapter: '', topic: '', details: '', chapterColor: '#E0E0FF', step: 'Step 1', practiced: false, mastered: false });
        setEditingSpot(null);
        setSnackbar({
          open: true,
          message: editingSpot ? 'Weak spot updated successfully!' : 'New weak spot added successfully!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Error saving weak spot. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      setSnackbar({
        open: true,
        message: 'Error saving weak spot. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // # Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingSpot) return;
    
    setIsSaving(true);
    try {
      // # Filter out the spot to delete
      const updatedSpotsMap = { ...weakSpotsMap };
      delete updatedSpotsMap[deletingSpot.id];
      
      // # Save to Firestore
      const success = await saveWeakSpots(updatedSpotsMap);
      
      if (success) {
        // # Update local state
        setWeakSpotsMap(updatedSpotsMap);
        
        // # Close dialog and show success message
        setOpenDeleteDialog(false);
        setDeletingSpot(null);
        setSnackbar({
          open: true,
          message: 'Weak spot deleted successfully!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Error deleting weak spot. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting weak spot:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting weak spot. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // # Update a specific field of a weak spot
  const updateWeakSpot = async (id, field, value) => {
    if (!id || !field) return;
    
    setIsSaving(true);
    const timestamp = new Date().toISOString();
    
    try {
      // # Update local state first
      const updatedSpotsMap = { ...weakSpotsMap };
      const updatedSpot = { ...updatedSpotsMap[id] };
      
      if (field === 'reviewCount') {
        updatedSpot.reviewCount = updatedSpot.reviewCount + 1;
      } else {
        updatedSpot[field] = value;
      }
      updatedSpot.updatedAt = timestamp;
      updatedSpotsMap[id] = updatedSpot;
      
      // # Save to Firestore
      const success = await saveWeakSpots(updatedSpotsMap);
      
      if (success) {
        // # Update local state
        setWeakSpotsMap(updatedSpotsMap);
        
        // # Show success message
        setSnackbar({
          open: true,
          message: `Weak spot ${field === 'reviewCount' ? 'review count increased' : 'updated'} successfully!`,
          severity: 'success'
        });
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating weak spot:', error);
      
      // # Revert local state on error
      setWeakSpotsMap(prev => {
        const updatedSpotsMap = { ...prev };
        const updatedSpot = { ...updatedSpotsMap[id] };
        
        if (field === 'reviewCount') {
          updatedSpot.reviewCount = updatedSpot.reviewCount - 1;
        } else {
          updatedSpot[field] = !value;
        }
        updatedSpot.updatedAt = timestamp;
        updatedSpotsMap[id] = updatedSpot;
        return updatedSpotsMap;
      });
      
      // # Show error message
      setSnackbar({
        open: true,
        message: 'Error updating weak spot. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // # Handle details update
  const handleDetailsUpdate = async () => {
    if (!viewingSpot) return;
    
    setIsSaving(true);
    const timestamp = new Date().toISOString();
    
    try {
      // # Update local state first
      const updatedSpotsMap = { ...weakSpotsMap };
      const updatedSpot = { ...updatedSpotsMap[viewingSpot.id] };
      
      updatedSpot.details = formData.details;
      updatedSpot.updatedAt = timestamp;
      updatedSpotsMap[viewingSpot.id] = updatedSpot;
      
      // # Save to Firestore
      const success = await saveWeakSpots(updatedSpotsMap);
      
      if (success) {
        // # Update local state
        setWeakSpotsMap(updatedSpotsMap);
        
        // # Close dialog and show success message
        setOpenDetailsDialog(false);
        setViewingSpot(null);
        setSnackbar({
          open: true,
          message: 'Details updated successfully!',
          severity: 'success'
        });
      } else {
        throw new Error('Failed to update details');
      }
    } catch (error) {
      console.error('Error updating details:', error);
      setSnackbar({
        open: true,
        message: 'Error updating details. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // # Calculate statistics from weakSpotsMap
  const calculateStats = () => {
    const spots = Object.values(weakSpotsMap);
    const totalSpots = spots.length;
    const practicedSpots = spots.filter(spot => spot.practiced).length;
    const masteredSpots = spots.filter(spot => spot.mastered).length;
    
    // # Calculate chapter distribution
    const chapterStats = spots.reduce((acc, spot) => {
      acc[spot.chapter] = (acc[spot.chapter] || 0) + 1;
      return acc;
    }, {});

    // # Calculate review trends (last 7 days)
    const reviewTrends = Array(7).fill(0).map((_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const count = spots.filter(spot => 
        spot.updatedAt && spot.updatedAt.startsWith(dateStr)
      ).length;
      return { date: dateStr, reviews: count };
    }).reverse();

    return {
      totalSpots,
      practicedSpots,
      masteredSpots,
      progressPercentage: totalSpots ? Math.round((masteredSpots / totalSpots) * 100) : 0,
      chapterStats,
      reviewTrends
    };
  };

  // # Scroll handlers
  const scrollToStats = () => {
    document.querySelector('.weakspots1_stats_section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stats = calculateStats();

  // # Chart colors
  const CHART_COLORS = ['#4caf50', '#2196f3', '#ff9800', '#e91e63', '#9c27b0'];

  return (
    <div className={`weakspots1_container ${isDarkMode ? 'weakspots1_dark_mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      {/* # Library Navigation Button */}
      <div className="weakspots1_library_nav_button" onClick={navigateToLibrary} title="Go to Step 1 Library">
        <FaBookMedical />
        <span>Library</span>
      </div>
      
      <div className="weakspots1_main">
        <div className="weakspots1_content">
          <div className="weakspots1_header_container">
            <div className="weakspots1_title_section">
              <Typography variant="h4" className="weakspots1_title">
                Weak Spots Tracker {isMentor && <span className="weakspots1_mentor_pill">Mentor</span>}
              </Typography>
            </div>
            
            <div className="weakspots1_buttons_section">
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddClick}
                className="weakspots1_add_button"
              >
                Add Weak Spot
              </Button>
            </div>

            <div className="weakspots1_buttons_section">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<BarChartIcon />}
                onClick={() => {
                  setShowStats(true);
                  setTimeout(() => {
                    scrollToStats();
                  }, 100);
                }}
                className="weakspots1_stats_nav_button"
              >
                Go to Stats
              </Button>
            </div>
          </div>

          {/* # Loading indicator */}
          {isLoading ? (
            <Box className="weakspots1_loading">
              <CircularProgress />
              <Typography variant="body1">Loading weak spots...</Typography>
            </Box>
          ) : (
            /* # Weak spots table */
            <TableContainer className="weakspots1_table_container">
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Chapter</TableCell>
                    <TableCell>Topic</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Reviews</TableCell>
                    <TableCell>Practiced</TableCell>
                    <TableCell>Mastered</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(weakSpotsMap).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body1" className="weakspots1_empty_message">
                          No weak spots added yet. Click the "Add Weak Spot" button to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object.entries(weakSpotsMap).map(([id, spot]) => (
                      <TableRow 
                        key={id}
                        style={{ backgroundColor: spot.chapterColor || 'transparent' }}
                      >
                        <TableCell>{spot.chapter}</TableCell>
                        <TableCell>{spot.topic}</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDetailsClick(spot)}
                            className="weakspots1_details_button"
                            disabled={isSaving}
                          >
                            Read More
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Box className="weakspots1_review_cell">
                            <span>{spot.reviewCount || 0}</span>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => updateWeakSpot(id, 'reviewCount')}
                              className="weakspots1_review_button"
                              disabled={isSaving}
                            >
                              +1
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={spot.practiced}
                            onChange={(e) => updateWeakSpot(id, 'practiced', e.target.checked)}
                            className="weakspots1_checkbox"
                            disabled={isSaving}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={spot.mastered}
                            onChange={(e) => updateWeakSpot(id, 'mastered', e.target.checked)}
                            className="weakspots1_checkbox"
                            disabled={isSaving}
                          />
                        </TableCell>
                        <TableCell>
                          <Box className="weakspots1_actions">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(spot)}
                              className="weakspots1_edit_button"
                              disabled={isSaving}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(spot)}
                              className="weakspots1_delete_button"
                              disabled={isSaving}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* # Stats Dashboard Section */}
          <section className="weakspots1_stats_section">
            <div className="weakspots1_stats_header">
              <Typography className="weakspots1_stats_title">
                Weak Spots Analytics Dashboard
              </Typography>
              <Button
                variant="contained"
                className="weakspots1_stats_nav_button"
                onClick={scrollToTop}
                startIcon={<ArrowUpwardIcon />}
              >
                Back to Table
              </Button>
            </div>

            {/* # Stats Cards Grid */}
            <div className="weakspots1_stats_grid">
              <div className="weakspots1_stats_card">
                <div className="weakspots1_stats_card_title">Total Weak Spots</div>
                <div className="weakspots1_stats_card_value">{stats.totalSpots}</div>
                <div className="weakspots1_stats_card_subtitle">Tracked topics</div>
              </div>

              <div className="weakspots1_stats_card">
                <div className="weakspots1_stats_card_title">Practiced</div>
                <div className="weakspots1_stats_card_value">{stats.practicedSpots}</div>
                <div className="weakspots1_stats_card_subtitle">Topics reviewed</div>
              </div>

              <div className="weakspots1_stats_card">
                <div className="weakspots1_stats_card_title">Mastered</div>
                <div className="weakspots1_stats_card_value">{stats.masteredSpots}</div>
                <div className="weakspots1_stats_card_subtitle">Topics mastered</div>
              </div>

              <div className="weakspots1_stats_card">
                <div className="weakspots1_stats_card_title">Progress</div>
                <div className="weakspots1_stats_card_value">{stats.progressPercentage}%</div>
                <div className="weakspots1_stats_card_subtitle">Overall completion</div>
              </div>
            </div>

            {/* # Charts Container */}
            <div className="weakspots1_charts_container">
              {/* # First Row - Full Width */}
              <div className="weakspots1_charts_row weakspots1_charts_row_full">
                <div className="weakspots1_chart_wrapper">
                  <h3 className="weakspots1_chart_title">Review Activity (Last 7 Days)</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.reviewTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="reviews" 
                        stroke="#2196f3" 
                        strokeWidth={2}
                        dot={{ fill: '#2196f3' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* # Second Row - Split */}
              <div className="weakspots1_charts_row weakspots1_charts_row_split">
                {/* # Chapter Distribution Chart */}
                <div className="weakspots1_chart_wrapper">
                  <h3 className="weakspots1_chart_title">Topics by Chapter</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={Object.entries(stats.chapterStats).map(([name, value], index) => ({
                          name,
                          value
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {Object.entries(stats.chapterStats).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* # Progress Distribution Chart */}
                <div className="weakspots1_chart_wrapper">
                  <h3 className="weakspots1_chart_title">Progress Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      { name: 'Not Started', value: stats.totalSpots - stats.practicedSpots },
                      { name: 'Practiced', value: stats.practicedSpots - stats.masteredSpots },
                      { name: 'Mastered', value: stats.masteredSpots }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value">
                        {
                          [
                            { name: 'Not Started', color: '#ff7675' },  // Soft red
                            { name: 'Practiced', color: '#74b9ff' },    // Soft blue
                            { name: 'Mastered', color: '#00b894' }      // Emerald green
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))
                        }
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* # Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => !isSaving && setOpenDialog(false)}
        className={`weakspots1_dialog ${isDarkMode ? 'weakspots1_dark_mode' : ''}`}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSpot ? 'Edit Weak Spot' : 'Add New Weak Spot'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" className="weakspots1_dialog_input">
            <InputLabel id="chapter-label">Chapter</InputLabel>
            <Select
              labelId="chapter-label"
              value={formData.chapter}
              onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
              label="Chapter"
              disabled={isSaving}
            >
              {STEP1_CHAPTERS.map((chapter) => (
                <MenuItem 
                  key={chapter.name} 
                  value={chapter.name}
                  style={{ backgroundColor: chapter.color }}
                >
                  {chapter.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            margin="dense"
            label="Topic"
            type="text"
            fullWidth
            value={formData.topic}
            onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
            className="weakspots1_dialog_input"
            disabled={isSaving}
          />
          
          <TextField
            margin="dense"
            label="Details"
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            value={formData.details}
            onChange={(e) => setFormData({ ...formData, details: e.target.value })}
            className="weakspots1_dialog_input"
            disabled={isSaving}
            placeholder="Enter brief notes about this weak spot..."
          />
          
          <Box sx={{ display: 'flex', mt: 2, gap: 2 }}>
            <FormControl className="weakspots1_dialog_input">
              <InputLabel id="practiced-label">Practiced</InputLabel>
              <Select
                labelId="practiced-label"
                value={formData.practiced}
                onChange={(e) => setFormData({ ...formData, practiced: e.target.value })}
                label="Practiced"
                disabled={isSaving}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl className="weakspots1_dialog_input">
              <InputLabel id="mastered-label">Mastered</InputLabel>
              <Select
                labelId="mastered-label"
                value={formData.mastered}
                onChange={(e) => setFormData({ ...formData, mastered: e.target.value })}
                label="Mastered"
                disabled={isSaving}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDialog(false)} 
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleFormSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.chapter || !formData.topic || isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* # Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !isSaving && setOpenDeleteDialog(false)}
        className={`weakspots1_delete_dialog ${isDarkMode ? 'weakspots1_dark_mode' : ''}`}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this weak spot? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* # Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => !isSaving && setOpenDetailsDialog(false)}
        className={`weakspots1_details_dialog ${isDarkMode ? 'weakspots1_dark_mode' : ''}`}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="weakspots1_details_dialog_title">
          <Typography variant="h6" component="div">
            {viewingSpot && viewingSpot.chapter}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {viewingSpot && viewingSpot.topic}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom color="primary" fontWeight={600}>
              Details:
            </Typography>
            <TextareaAutosize
              minRows={3}
              maxRows={15}
              value={formData.details}
              onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              className="weakspots1_details_textarea"
              disabled={isSaving}
              placeholder="Enter detailed notes about this weak spot..."
            />
          </Box>
          
          {viewingSpot && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box className="weakspots1_details_info">
                <Box className="weakspots1_details_info_item">
                  <Typography variant="body2" color="textSecondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(viewingSpot.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box className="weakspots1_details_info_item">
                  <Typography variant="body2" color="textSecondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {new Date(viewingSpot.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box className="weakspots1_details_info_item">
                  <Typography variant="body2" color="textSecondary">
                    Review Count
                  </Typography>
                  <Typography variant="body1" color="primary" fontWeight={600}>
                    {viewingSpot.reviewCount || 0}
                  </Typography>
                </Box>
                <Box className="weakspots1_details_info_item">
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Checkbox
                      checked={viewingSpot.practiced}
                      disabled
                      size="small"
                    />
                    <Typography variant="body2">Practiced</Typography>
                    <Checkbox
                      checked={viewingSpot.mastered}
                      disabled
                      size="small"
                    />
                    <Typography variant="body2">Mastered</Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions className="weakspots1_details_dialog_actions">
          <Button 
            onClick={() => setOpenDetailsDialog(false)} 
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDetailsUpdate} 
            variant="contained" 
            color="primary"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Saving...' : 'Update Details'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* # Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        className={isDarkMode ? 'weakspots1_dark_mode' : ''}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default WeakSpots1;
